import { useLocale, type Locale } from '@/lib/locale'

/**
 * Shared UI strings (chrome, course player, library, tools) for the pages a
 * bilingual face reaches. English is the source and must stay byte-identical
 * to what the English-only faces rendered before this file existed; Arabic
 * is the full translation. Slide and article copy live next to their content
 * (`*.copy.ts` `ar` blocks, `data/articles/*.ts` `ar` blocks), not here.
 *
 * Usage: `const t = useUi()` then `t.slideContinue`. Strings with a value
 * are functions, e.g. `t.slideCounter(3, 14)`.
 */
const en = {
  // ── Course player (SlideWizard) ──────────────────────────────────────
  slideExitAria: 'Exit chapter',
  slideBack: 'Back',
  slideContinue: 'Continue',
  slideFinish: 'Finish',
  slideCounter: (current: number, total: number) => `${current}/${total}`,
  leaveTitle: 'Leave chapter?',
  leaveBody: 'Your progress is saved on this device. You can pick up where you left off.',
  leaveKeepGoing: 'Keep going',
  leaveLeave: 'Leave',

  // ── Chapter feedback ────────────────────────────────────────────────
  feedbackAria: 'Chapter feedback',
  feedbackTitle: 'Was this chapter helpful?',
  feedbackYes: 'Yes, helpful',
  feedbackNo: 'Could be better',
  feedbackPrompt: 'Any questions or feedback?',
  feedbackOptional: '(optional)',
  feedbackPlaceholder: "What worked, what didn't, what you'd like next time…",
  feedbackPrivacy: 'Avoid sharing personal info. We redact it before anyone reads it.',
  feedbackSend: 'Send feedback',
  feedbackSending: 'Sending…',
  feedbackError: "We couldn't save that just now. You can try again in a moment.",
  feedbackThanks: 'Thanks. We read every note.',
  feedbackPrivate: 'Your feedback is private to ScamDefense and our content sponsors. We never publish it.',
  feedbackPrivateBank: 'Your feedback is private to the program team. We never publish it.',
  /** GranGuard face: names the brand and, on a partner face, the partner. */
  feedbackPrivateWith: (brand: string, partner?: string) =>
    partner
      ? `Your feedback is private to ${brand} and ${partner}. We never publish it.`
      : `Your feedback is private to ${brand}. We never publish it.`,
  feedbackUrgentBefore: 'If you need urgent help right now, call the AARP Fraud Watch Helpline at ',
  feedbackUrgentAfter: ", or 911 if you're in immediate danger.",
  feedbackUrgentBankBefore: 'If you need urgent help right now, call our fraud line at ',
  feedbackUrgentBankAfter: ', or 999 if you are in immediate danger.',

  // ── Reset progress ─────────────────────────────────────────────────
  resetLabel: 'Reset progress',
  resetConfirmTitle: 'Reset course progress?',
  resetConfirmBody: 'This will clear your progress across every chapter. It can’t be undone.',
  resetConfirm: 'Reset course progress',
  resetCancel: 'Cancel',

  // ── Chapter overview ────────────────────────────────────────────────
  chapterLabel: (n: number) => `Chapter ${n}`,
  chapterAria: (n: number, title: string) => `Chapter ${n}: ${title}`,
  prevChapterAria: (title: string) => `Previous chapter: ${title}`,
  nextChapterAria: (title: string) => `Next chapter: ${title}`,
  home: 'Home',
  startChapter: 'Start chapter',
  continueChapter: 'Continue chapter',
  statusComplete: 'Complete',
  statusInProgress: 'In progress',
  statusNotStarted: 'Not started',
  ofSlides: (total: number) => ` of ${total} slides`,
  statTime: 'Time',
  statSlides: 'Slides',
  approxMinutes: (m: number) => `~${m} min`,
  whatYoullCover: 'What you’ll cover',
  chapterMeta: (m: number, slides: number) =>
    `~${m} minutes, ${slides} short screens. Pause anytime, your spot is saved on this device.`,
  keyPoints: 'Key points',

  // ── Course index (/basics) ─────────────────────────────────────────
  basicsEyebrow: 'Scam Defense Fundamentals',
  basicsTitle: 'Learn to spot scams in 6 short chapters.',
  basicsIntro:
    'Practical training in plain English. No accounts required. Start anywhere, each chapter stands on its own.',
  minutes: (m: number) => `${m} min`,
  percentComplete: (p: number) => `${p}% complete`,

  // ── Scam library ───────────────────────────────────────────────────
  libraryTitle: 'The Scam Library',
  libraryIntro:
    'Plain-English summaries of how each scam works, what to watch for, and what to do if it happens to you.',
  librarySearch: 'Search scams',
  libraryLayoutAria: 'Card layout',
  librarySideLayout: 'Side-by-side layout',
  libraryTopLayout: 'Top-thumbnail layout',
  loading: 'Loading…',
  libraryError: 'We couldn’t reach the scam library. Try refreshing.',
  libraryNoMatch: (q: string) => `No scams match “${q}”.`,
  clearSearch: 'Clear search',
  allCategories: 'All',
  libraryNoMatchCategory: 'No scams in this category yet.',
  learnMore: 'Learn more',
  backToLibrary: 'Back to library',
  entryError: 'We couldn’t load this entry. Try refreshing.',
  atAGlance: 'At a glance',
  riskLevel: 'Risk level',
  riskLow: 'Low risk',
  riskMedium: 'Medium risk',
  riskHigh: 'High risk',
  riskCritical: 'Critical risk',
  whereYoullSeeIt: 'Where you’ll see it',
  channelPhone: 'Phone',
  channelSms: 'Text message',
  channelEmail: 'Email',
  channelWebsite: 'Website',
  channelApp: 'App',
  channelMail: 'Postal mail',
  channelInPerson: 'In person',
  channelSocial: 'Social media',
  channelMessaging: 'Messaging apps',
  category: 'Category',
  tags: 'Tags',
  happenedToYou: 'Think this happened to you?',
  happenedBody: 'Move quickly. Chapter 6 walks through the first hour, day, and week.',
  openChapter6: 'Open chapter 6 →',
  quickActions: 'Quick actions',
  share: 'Share',
  linkCopied: 'Link copied!',
  relatedScams: 'Related scams',
  print: 'Print',
  reportToFtc: 'Report this scam to the FTC',
  reportToMoi: 'Report this scam to the Ministry of Interior',
  checkSuspicious: 'Check a suspicious message',
  warnFamily: 'Warn family',
  warnSubject: (title: string) => `Heads up: watch out for "${title}"`,
  warnBody: (url: string) =>
    `I read about this scam and thought you should know what to watch for:\n\n${url}`,

  // ── Tools ──────────────────────────────────────────────────────────
  toolsEyebrow: 'Tools',
  toolsTitle: 'Quick checks. No sign-up required.',
  toolsIntro: 'Quick tools to help you decide whether something’s safe before you click, type, or send.',
  openTool: 'Open tool',
  preview: 'Preview',
  toolScamCheckerLabel: 'Scam Checker',
  toolScamCheckerBody:
    'Paste a suspicious link, message, or situation and get a plain-English read on the warning signs.',
  toolPasswordLabel: 'Password Check',
  toolPasswordBody:
    'See if a password has shown up in known data breaches. Runs entirely in your browser, we never see what you typed.',
  toolCharityLabel: 'Charity Checker',
  toolCharityBody: 'Verify that a charity is real before you donate.',

  // ── Scam checker page ──────────────────────────────────────────────
  scTitle: 'Scam Checker',
  scIntro:
    'Paste a suspicious link or message, or upload a picture, recording or video, and get a plain-English read on the warning signs, including signs of AI voice cloning and deepfakes.',
  scAssurance: 'Nothing you paste is made public. We use it only to run this check.',
  scInputLabel: 'Link, message or email text',
  scPlaceholder: 'Paste the link, or the whole message, exactly as you received it…',
  scCheck: 'Check it',
  scChecking: 'Checking… (usually 10 to 20 seconds)',
  scUpload: 'Upload a picture, audio, or video',
  scErrRate: 'You’ve made a lot of checks in a short time. Give it a few minutes and try again.',
  scErrGeneric: 'We couldn’t run the check just now. Please try again in a moment.',
  scErrTimeout:
    'The check took too long to finish. Please try again. Until you get a result, don’t act on the message.',
  scErrTooLong: 'That’s too long to check in one go. Paste the part with the link, or the part asking for money or details.',
  scErrBadInput: 'We couldn’t check that. Try pasting just the link, or the message text exactly as you received it.',
  scThinInput:
    'There isn’t enough here to check. Paste the whole message, including who it’s from and any link, or paste the link itself.',
  scErrIncomplete:
    'We couldn’t finish this check, so we can’t give you a result. That is not a sign that it’s safe. Don’t act on it until you’ve checked another way.',
  scErrFile: 'We couldn’t analyze that file just now. Please try again in a moment.',
  scErrType: 'Please choose a JPEG/PNG/WebP picture, an MP3/M4A/WAV audio file, or an MP4/WebM video.',
  scErrImageSize: 'Pictures must be under 10MB.',
  scErrMediaSize: 'Audio and video must be under 25MB.',
  scStageLink: 'Checking the link… (usually 10 to 20 seconds)',
  scStageText: 'Reading the message… (usually 10 to 20 seconds)',
  scTextNotes: [
    'Reading the message…',
    'Looking for pressure and urgency tactics…',
    'Checking any links it contains…',
    'Comparing against known scam patterns…',
    'Building your plain-English report…',
  ],
  scLinkNotes: [
    'Opening the page…',
    'Checking threat databases…',
    'Looking up when the address was registered…',
    'Comparing the site with public records…',
    'Reading what the page asks you to do…',
    'Building your plain-English report…',
  ],
  scStageImage: 'Analyzing the picture…',
  scStageVideo: 'Reading the video…',
  scStageAudio: 'Reading the audio…',
  scStageUpload: 'Uploading…',
  scStageVideoAnalyze: 'Analyzing voice and frames… (up to 2 minutes)',
  scStageAudioAnalyze: 'Analyzing the voice… (up to 2 minutes)',
  scVerdictSafe: 'No warning signs found',
  scVerdictCaution: 'Treat with caution',
  scVerdictDanger: 'Strong signs of a scam',
  scWhatWeLookedAt: 'What we looked at',
  scPublicRecord: 'See the public record',
  scWhatWeNoticed: 'What we noticed',
  scWhatToDo: 'What to do',
  scOpen: 'Open',
  scFooter:
    'Checks combine link and message analysis with live threat databases and an AI review, with the final call made by fixed rules rather than the AI alone. No result is a guarantee: something can be dangerous before any database knows about it, so if it feels wrong, don’t act on it.',
  scHangTight: 'Hang tight, this only takes a moment.',
  scNotes: [
    'Listening to the audio track…',
    'Transcribing what is said…',
    'Looking for pressure and urgency tactics…',
    'Comparing against known scam patterns…',
    'Checking for signs of AI generation…',
    'Building your plain-English report…',
  ],

  // ── Articles ───────────────────────────────────────────────────────
  articlesTitle: 'Scam prevention articles.',
  articlesIntro: 'How scams work, how to spot them, and how to recover if one slips through.',
  backToArticles: 'Back to articles',
  minRead: (m: number) => `${m} min read`,
  read: 'Read',
  readArticle: 'Read article',
  noPosts: 'No posts yet. New breakdowns land here first.',
  seeAll: 'See all →',
  copyLink: 'Copy link',
  copied: 'Copied',
  shareEmail: 'Share via email',
  contents: 'Contents',
  articleNotFound: 'We couldn’t find that article.',
  browseAllArticles: 'Browse all articles →',

  // ── Charts (chapter 1 revenue comparison) ──────────────────────────
  chartCounterfeit: 'Counterfeit Goods',
  chartDrugs: 'Drug Trade',
  chartScams: 'Scams',
  chartRevenue: 'Revenue',

  // ── Privacy ────────────────────────────────────────────────────────
  privacyEyebrow: 'Privacy',
  privacyTitle: 'Privacy Policy',
  privacyUpdated: 'Last updated: September 24, 2026',

  // ── Common ─────────────────────────────────────────────────────────
  languageToggleAria: 'Switch language',
  openMenu: 'Open menu',
  closeMenu: 'Close menu',
  close: 'Close',
  closePanel: 'Close panel',
  cancel: 'Cancel',
}

export type UiStrings = typeof en

const ar: UiStrings = {
  slideExitAria: 'الخروج من الفصل',
  slideBack: 'رجوع',
  slideContinue: 'متابعة',
  slideFinish: 'إنهاء',
  slideCounter: (current, total) => `${current}/${total}`,
  leaveTitle: 'هل تريد مغادرة الفصل؟',
  leaveBody: 'تقدّمك محفوظ على هذا الجهاز. يمكنك المتابعة من حيث توقفت.',
  leaveKeepGoing: 'أكمل',
  leaveLeave: 'مغادرة',

  feedbackAria: 'رأيك في الفصل',
  feedbackTitle: 'هل كان هذا الفصل مفيداً؟',
  feedbackYes: 'نعم، مفيد',
  feedbackNo: 'يمكن أن يكون أفضل',
  feedbackPrompt: 'هل لديك أسئلة أو ملاحظات؟',
  feedbackOptional: '(اختياري)',
  feedbackPlaceholder: 'ما الذي أعجبك، وما الذي لم يعجبك، وما الذي تودّ رؤيته في المرة القادمة…',
  feedbackPrivacy: 'تجنّب مشاركة معلومات شخصية. نحذفها قبل أن يقرأها أي شخص.',
  feedbackSend: 'إرسال الملاحظات',
  feedbackSending: 'جارٍ الإرسال…',
  feedbackError: 'تعذّر حفظ ملاحظاتك الآن. يمكنك المحاولة مرة أخرى بعد قليل.',
  feedbackThanks: 'شكراً لك. نقرأ كل ملاحظة.',
  feedbackPrivate: 'ملاحظاتك خاصة ولا يطّلع عليها سوى فريق البرنامج. لا ننشرها أبداً.',
  feedbackPrivateBank: 'ملاحظاتك خاصة ولا يطّلع عليها سوى فريق البرنامج. لا ننشرها أبداً.',
  feedbackPrivateWith: () => 'ملاحظاتك خاصة ولا يطّلع عليها سوى فريق البرنامج. لا ننشرها أبداً.',
  feedbackUrgentBefore: 'إذا كنت بحاجة إلى مساعدة عاجلة الآن، فاتصل بخط الاحتيال لدينا على ',
  feedbackUrgentAfter: '، أو بالرقم 999 إذا كنت في خطر مباشر.',
  feedbackUrgentBankBefore: 'إذا كنت بحاجة إلى مساعدة عاجلة الآن، فاتصل بخط الاحتيال لدينا على ',
  feedbackUrgentBankAfter: '، أو بالرقم 999 إذا كنت في خطر مباشر.',

  resetLabel: 'إعادة ضبط التقدّم',
  resetConfirmTitle: 'هل تريد إعادة ضبط تقدّم الدورة؟',
  resetConfirmBody: 'سيؤدي هذا إلى مسح تقدّمك في جميع الفصول. لا يمكن التراجع عن ذلك.',
  resetConfirm: 'إعادة ضبط تقدّم الدورة',
  resetCancel: 'إلغاء',

  chapterLabel: n => `الفصل ${n}`,
  chapterAria: (n, title) => `الفصل ${n}: ${title}`,
  prevChapterAria: title => `الفصل السابق: ${title}`,
  nextChapterAria: title => `الفصل التالي: ${title}`,
  home: 'الرئيسية',
  startChapter: 'ابدأ الفصل',
  continueChapter: 'تابع الفصل',
  statusComplete: 'مكتمل',
  statusInProgress: 'قيد التقدّم',
  statusNotStarted: 'لم يبدأ بعد',
  ofSlides: total => ` من ${total} شريحة`,
  statTime: 'المدة',
  statSlides: 'الشرائح',
  approxMinutes: m => `نحو ${m} دقائق`,
  whatYoullCover: 'ما ستتعلّمه',
  chapterMeta: (m, slides) =>
    `نحو ${m} دقائق، ${slides} شاشة قصيرة. يمكنك التوقف في أي وقت، وموضعك محفوظ على هذا الجهاز.`,
  keyPoints: 'النقاط الأساسية',

  basicsEyebrow: 'أساسيات الحماية من الاحتيال',
  basicsTitle: 'تعلّم كشف عمليات الاحتيال في 6 فصول قصيرة.',
  basicsIntro:
    'تدريب عملي بلغة واضحة. لا يتطلب إنشاء حساب. ابدأ من أي فصل، فكل فصل مستقل بذاته.',
  minutes: m => `${m} دقائق`,
  percentComplete: p => `${p}% مكتمل`,

  libraryTitle: 'مكتبة الاحتيال',
  libraryIntro:
    'شرح مبسّط لكيفية عمل كل عملية احتيال، وما يجب الانتباه إليه، وما تفعله إذا حدث ذلك لك.',
  librarySearch: 'ابحث في عمليات الاحتيال',
  libraryLayoutAria: 'تنسيق البطاقات',
  librarySideLayout: 'تنسيق جانبي',
  libraryTopLayout: 'تنسيق بصورة علوية',
  loading: 'جارٍ التحميل…',
  libraryError: 'تعذّر الوصول إلى مكتبة الاحتيال. حاول تحديث الصفحة.',
  libraryNoMatch: q => `لا توجد نتائج تطابق "${q}".`,
  clearSearch: 'مسح البحث',
  allCategories: 'الكل',
  libraryNoMatchCategory: 'لا توجد عمليات احتيال في هذا التصنيف بعد.',
  learnMore: 'اعرف المزيد',
  backToLibrary: 'العودة إلى المكتبة',
  entryError: 'تعذّر تحميل هذه الصفحة. حاول تحديث الصفحة.',
  atAGlance: 'نظرة سريعة',
  riskLevel: 'مستوى الخطورة',
  riskLow: 'خطورة منخفضة',
  riskMedium: 'خطورة متوسطة',
  riskHigh: 'خطورة عالية',
  riskCritical: 'خطورة حرجة',
  whereYoullSeeIt: 'أين ستصادفه',
  channelPhone: 'مكالمة هاتفية',
  channelSms: 'رسالة نصية',
  channelEmail: 'بريد إلكتروني',
  channelWebsite: 'موقع إلكتروني',
  channelApp: 'تطبيق',
  channelMail: 'بريد عادي',
  channelInPerson: 'وجهاً لوجه',
  channelSocial: 'وسائل التواصل الاجتماعي',
  channelMessaging: 'تطبيقات المراسلة',
  category: 'التصنيف',
  tags: 'الوسوم',
  happenedToYou: 'هل تعتقد أن هذا حدث لك؟',
  happenedBody: 'تصرّف بسرعة. يشرح الفصل السادس ما تفعله في الساعة الأولى واليوم الأول والأسبوع الأول.',
  openChapter6: 'افتح الفصل السادس ←',
  quickActions: 'إجراءات سريعة',
  share: 'مشاركة',
  linkCopied: 'تم نسخ الرابط!',
  relatedScams: 'عمليات احتيال ذات صلة',
  print: 'طباعة',
  reportToFtc: 'أبلغ عن هذا الاحتيال',
  reportToMoi: 'أبلغ وزارة الداخلية عن هذا الاحتيال',
  checkSuspicious: 'افحص رسالة مريبة',
  warnFamily: 'حذّر عائلتك',
  warnSubject: title => `تنبيه: احذر من "${title}"`,
  warnBody: url => `قرأت عن عملية الاحتيال هذه ورأيت أن تعرف ما الذي عليك الانتباه إليه:\n\n${url}`,

  toolsEyebrow: 'الأدوات',
  toolsTitle: 'فحوصات سريعة. دون الحاجة إلى تسجيل.',
  toolsIntro: 'أدوات سريعة تساعدك على تحديد ما إذا كان شيء ما آمناً قبل أن تنقر أو تكتب أو ترسل.',
  openTool: 'افتح الأداة',
  preview: 'معاينة',
  toolScamCheckerLabel: 'فاحص الاحتيال',
  toolScamCheckerBody:
    'الصق رابطاً أو رسالة أو موقفاً مريباً واحصل على تقييم واضح لما إذا كان يبدو حقيقياً.',
  toolPasswordLabel: 'فحص كلمة المرور',
  toolPasswordBody:
    'تحقق مما إذا كانت كلمة المرور قد ظهرت في تسريبات بيانات معروفة. يعمل بالكامل داخل متصفحك، ولا نرى ما كتبته أبداً.',
  toolCharityLabel: 'فاحص الجمعيات الخيرية',
  toolCharityBody: 'تحقق من أن الجمعية الخيرية حقيقية قبل أن تتبرع.',

  scTitle: 'فاحص الاحتيال',
  scIntro:
    'الصق رابطاً أو رسالة مريبة، أو ارفع مقطع فيديو، واحصل على تقييم واضح لما إذا كان يبدو حقيقياً، بما في ذلك علامات استنساخ الصوت بالذكاء الاصطناعي والتزييف العميق.',
  scAssurance: 'لا يُنشر أي شيء تلصقه هنا. نستخدمه فقط لإجراء هذا الفحص.',
  scInputLabel: 'الرابط أو نص الرسالة أو البريد الإلكتروني',
  scPlaceholder: 'الصق الرابط أو الرسالة كاملة، تماماً كما وصلتك…',
  scCheck: 'افحصه',
  scChecking: 'جارٍ الفحص… (عادةً من 10 إلى 20 ثانية)',
  scUpload: 'ارفع صورة أو مقطعاً صوتياً أو فيديو',
  scErrRate: 'لقد أجريت عدداً كبيراً من الفحوصات في وقت قصير. انتظر بضع دقائق ثم حاول مرة أخرى.',
  scErrGeneric: 'تعذّر إجراء الفحص الآن. يرجى المحاولة مرة أخرى بعد قليل.',
  scErrTimeout: 'استغرق الفحص وقتاً أطول من اللازم. يرجى المحاولة مرة أخرى. لا تتصرّف بناءً على الرسالة حتى تحصل على نتيجة.',
  scErrTooLong: 'النص أطول من أن يُفحص دفعة واحدة. الصق الجزء الذي يحتوي على الرابط، أو الجزء الذي يطلب مالاً أو بيانات.',
  scErrBadInput: 'تعذّر فحص ذلك. جرّب لصق الرابط وحده، أو نص الرسالة كما وصلك تماماً.',
  scThinInput: 'لا يوجد ما يكفي هنا للفحص. الصق الرسالة كاملة، بما في ذلك المرسل وأي رابط، أو الصق الرابط وحده.',
  scErrIncomplete: 'تعذّر إكمال هذا الفحص، لذا لا يمكننا إعطاؤك نتيجة. هذا لا يعني أنه آمن. لا تتصرّف بناءً عليه حتى تتحقّق بطريقة أخرى.',
  scErrFile: 'تعذّر تحليل هذا الملف الآن. يرجى المحاولة مرة أخرى بعد قليل.',
  scErrType: 'يرجى اختيار صورة بصيغة JPEG أو PNG أو WebP، أو ملف صوتي MP3 أو M4A أو WAV، أو فيديو MP4 أو WebM.',
  scErrImageSize: 'يجب أن يكون حجم الصورة أقل من 10 ميغابايت.',
  scErrMediaSize: 'يجب أن يكون حجم الملف الصوتي أو الفيديو أقل من 25 ميغابايت.',
  scStageLink: 'جارٍ فحص الرابط… (عادةً من 10 إلى 20 ثانية)',
  scStageText: 'جارٍ قراءة الرسالة… (عادةً من 10 إلى 20 ثانية)',
  scTextNotes: [
    'جارٍ قراءة الرسالة…',
    'جارٍ البحث عن أساليب الضغط والاستعجال…',
    'جارٍ التحقق من أي روابط فيها…',
    'جارٍ المقارنة مع أنماط الاحتيال المعروفة…',
    'جارٍ إعداد تقريرك بلغة واضحة…',
  ],
  scLinkNotes: [
    'جارٍ فتح الصفحة…',
    'جارٍ التحقق من قواعد بيانات التهديدات…',
    'جارٍ البحث عن تاريخ تسجيل العنوان…',
    'جارٍ مقارنة الموقع مع السجلات العامة…',
    'جارٍ قراءة ما تطلبه الصفحة منك…',
    'جارٍ إعداد تقريرك بلغة واضحة…',
  ],
  scStageImage: 'جارٍ تحليل الصورة…',
  scStageVideo: 'جارٍ قراءة الفيديو…',
  scStageAudio: 'جارٍ قراءة المقطع الصوتي…',
  scStageUpload: 'جارٍ الرفع…',
  scStageVideoAnalyze: 'جارٍ تحليل الصوت والإطارات… (حتى دقيقتين)',
  scStageAudioAnalyze: 'جارٍ تحليل الصوت… (حتى دقيقتين)',
  scVerdictSafe: 'لم نجد علامات تحذير',
  scVerdictCaution: 'تعامل معه بحذر',
  scVerdictDanger: 'علامات قوية على الاحتيال',
  scWhatWeLookedAt: 'ما فحصناه',
  scPublicRecord: 'اطّلع على السجل العام',
  scWhatWeNoticed: 'ما لاحظناه',
  scWhatToDo: 'ما يجب فعله',
  scOpen: 'فتح',
  scFooter:
    'تجمع الفحوصات بين تحليل الروابط والرسائل وقواعد بيانات التهديدات الحيّة ومراجعة بالذكاء الاصطناعي، ويُتخذ القرار النهائي وفق قواعد ثابتة لا بالذكاء الاصطناعي وحده. لا يُعدّ أي تقييم ضماناً: فقد يكون شيء ما خطيراً قبل أن تعرفه أي قاعدة بيانات، لذا إذا شعرت أن هناك خطأً ما، فلا تتصرّف بناءً عليه.',
  scHangTight: 'انتظر قليلاً، لن يستغرق هذا سوى لحظات.',
  scNotes: [
    'جارٍ الاستماع إلى المسار الصوتي…',
    'جارٍ تفريغ ما يُقال…',
    'جارٍ البحث عن أساليب الضغط والاستعجال…',
    'جارٍ المقارنة مع أنماط الاحتيال المعروفة…',
    'جارٍ التحقق من علامات التوليد بالذكاء الاصطناعي…',
    'جارٍ إعداد تقريرك بلغة واضحة…',
  ],

  articlesTitle: 'مقالات الوقاية من الاحتيال.',
  articlesIntro: 'كيف تعمل عمليات الاحتيال، وكيف تكتشفها، وكيف تتعافى إذا نجحت إحداها في الوصول إليك.',
  backToArticles: 'العودة إلى المقالات',
  minRead: m => `${m} دقائق قراءة`,
  read: 'اقرأ',
  readArticle: 'اقرأ المقال',
  noPosts: 'لا توجد مقالات بعد. المقالات الجديدة تظهر هنا أولاً.',
  seeAll: 'عرض الكل ←',
  copyLink: 'نسخ الرابط',
  copied: 'تم النسخ',
  shareEmail: 'مشاركة عبر البريد الإلكتروني',
  contents: 'المحتويات',
  articleNotFound: 'تعذّر العثور على هذا المقال.',
  browseAllArticles: 'تصفّح جميع المقالات ←',

  chartCounterfeit: 'السلع المقلّدة',
  chartDrugs: 'تجارة المخدرات',
  chartScams: 'الاحتيال',
  chartRevenue: 'الإيرادات',

  privacyEyebrow: 'الخصوصية',
  privacyTitle: 'سياسة الخصوصية',
  privacyUpdated: 'آخر تحديث: 1 مايو 2026',

  languageToggleAria: 'تبديل اللغة',
  openMenu: 'فتح القائمة',
  closeMenu: 'إغلاق القائمة',
  close: 'إغلاق',
  closePanel: 'إغلاق اللوحة',
  cancel: 'إلغاء',
}

const DICTIONARIES: Record<Locale, UiStrings> = { en, ar }

export function uiStrings(locale: Locale): UiStrings {
  return DICTIONARIES[locale]
}

export function useUi(): UiStrings {
  return DICTIONARIES[useLocale()]
}
